import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Panel, PanelHeader } from "@/components/common/Panel";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { intelApi, apiError } from "@/lib/intel";
import { cn } from "@/lib/utils";
import { FileText, Check, Copy, UserPlus, Sparkles, Download } from "lucide-react";

const FIELDS = [["parentage", "Parentage"], ["age", "Age"], ["address", "Address"]];
const SIDE = { PETITIONER: "bg-copper/10 text-copper", RESPONDENT: "bg-navy/10 text-navy", OTHER: "bg-secondary text-ink-2" };

function Candidate({ c, onApply, disabled }) {
  return (
    <button disabled={disabled} onClick={onApply} title={`From ${c.documentName}${c.docType ? ` (${c.docType})` : ""}`} data-testid="cause-title-candidate"
      className="group flex max-w-full items-center gap-1 rounded-full border border-dashed border-copper/50 bg-card px-2 py-0.5 text-left text-[11px] text-ink hover:bg-copper/10 disabled:opacity-60">
      <Sparkles className="h-3 w-3 shrink-0 text-copper" /><span className="truncate">{c.value}</span>
      <span className="shrink-0 text-[10px] text-ink-muted">· {c.docType || c.documentName}</span>
      <Check className="hidden h-3 w-3 shrink-0 text-copper group-hover:inline" />
    </button>
  );
}

function PartyCard({ p, onApply, canEdit }) {
  const src = (f) => p.causeTitleSources?.filter((s) => s.field === f).slice(-1)[0];
  return (
    <div className="rounded-lg border border-line bg-card p-3 text-sm" data-testid={`cause-title-party-${p.id}`}>
      <div className="flex items-center gap-2"><span className="font-medium text-ink">{p.name}</span><span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold", SIDE[p.side])}>{p.role}</span>
        {p.candidateCount > 0 && <span className="ml-auto text-[10px] text-copper" data-testid="cause-title-candidate-count">{p.candidateCount} candidate(s) from documents</span>}</div>
      {p.candidates?.name?.length > 0 && <div className="mt-1 flex flex-wrap gap-1"><span className="text-[11px] text-ink-muted">Name as in documents:</span>{p.candidates.name.map((c, i) => <Candidate key={i} c={c} disabled={!canEdit} onApply={() => onApply(p.id, "name", c)} />)}</div>}
      <div className="mt-2 grid gap-2 md:grid-cols-3">
        {FIELDS.filter(([f]) => !p.entity || f === "address").map(([f, label]) => (
          <div key={f} className="rounded border border-line/70 p-2" data-testid={`cause-title-field-${f}-${p.id}`}>
            <div className="text-[10px] uppercase tracking-wide text-ink-muted">{label}</div>
            <div className={cn("text-xs", p[f] ? "text-ink" : "text-warning")}>{p[f] || "[verify] not on record"}</div>
            {src(f) && <div className="text-[10px] text-ink-muted">{src(f).sourceType === "DOCUMENT_SUPPORTED" ? `from ${src(f).documentName}` : "stated by counsel"}</div>}
            {p.candidates?.[f]?.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{p.candidates[f].map((c, i) => <Candidate key={i} c={c} disabled={!canEdit} onApply={() => onApply(p.id, f, c)} />)}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Unmatched({ u, onAdd, canEdit }) {
  const [sideSel, setSideSel] = useState(u.suggestedSide === "RESPONDENT" ? "Respondent" : u.suggestedSide === "PETITIONER" ? "Petitioner" : "Other");
  return (
    <div className="flex flex-wrap items-center gap-2 rounded border border-line bg-card px-3 py-2 text-xs" data-testid="cause-title-unmatched">
      <FileText className="h-3.5 w-3.5 text-copper" />
      <div className="min-w-0 flex-1"><span className="font-medium text-ink">{u.name}</span>{u.role && <span className="text-ink-muted"> · {u.role} in {u.docType || "document"}</span>}
        <div className="text-[11px] text-ink-2">{[u.parentage, u.age && `aged ${u.age}`, u.address].filter(Boolean).join(" · ") || "No further particulars extracted"} <span className="text-ink-muted">— {u.documentName}</span></div></div>
      {canEdit && <>
        <select value={sideSel} onChange={(e) => setSideSel(e.target.value)} className="h-7 rounded border border-line bg-card px-1 text-[11px]" data-testid="cause-title-unmatched-role">{["Petitioner", "Respondent", "Witness", "Other"].map((r) => <option key={r}>{r}</option>)}</select>
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => onAdd(u, sideSel)} data-testid="cause-title-add-party"><UserPlus className="mr-1 h-3 w-3" />Add as party</Button>
      </>}
    </div>
  );
}

export function CauseTitleBuilder({ matterId, onChanged }) {
  const { can } = useApp();
  const [data, setData] = useState(null);
  const canEdit = can("matter.edit");
  const load = () => intelApi.causeTitle(matterId).then(setData).catch(() => setData(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [matterId]);
  const apply = async (partyId, field, c) => {
    try { await intelApi.applyCauseTitle(matterId, { partyId, fields: { [field]: c.value }, sourceDocumentId: c.documentId }); toast.success(`${field} confirmed from ${c.documentName}.`); load(); onChanged?.(); } catch (e) { toast.error(apiError(e, "Could not apply")); }
  };
  const add = async (u, role) => {
    try { await intelApi.applyCauseTitle(matterId, { fields: { name: u.name, role, parentage: u.parentage || undefined, age: u.age || undefined, address: u.address || undefined }, sourceDocumentId: u.documentId }); toast.success(`${u.name} added as ${role}.`); load(); onChanged?.(); } catch (e) { toast.error(apiError(e, "Could not add party")); }
  };
  const copy = () => { navigator.clipboard?.writeText(data.render.text); toast.success("Cause title copied."); };
  const download = async () => { try { await intelApi.downloadCauseTitle(matterId, data.matter.caseNumber); toast.success("Cause title DOCX downloaded."); } catch (e) { toast.error(apiError(e, "Download failed")); } };
  if (data === null) return <Panel className="p-4 text-sm text-ink-muted">Loading cause title…</Panel>;
  if (data === false) return null;
  const r = data.render;
  return (
    <div className="space-y-4" data-testid="cause-title-builder">
      <Panel>
        <PanelHeader title="Cause Title Builder">
          <span className="text-xs text-ink-muted">{r.placeholders ? `${r.placeholders} value(s) still to verify` : "All listed values confirmed"}</span>
          <Button size="sm" variant="outline" className="h-7" onClick={copy} data-testid="cause-title-copy"><Copy className="mr-1 h-3 w-3" />Copy</Button>
          <Button size="sm" className="h-7" onClick={download} data-testid="cause-title-download"><Download className="mr-1 h-3 w-3" />DOCX</Button>
        </PanelHeader>
        <div className="grid gap-4 p-4 lg:grid-cols-5">
          <div className="space-y-3 lg:col-span-3">
            {data.parties.length === 0 && <p className="text-sm text-ink-muted">No parties on record yet. Add them from document candidates below or via Quick Add.</p>}
            {data.parties.map((p) => <PartyCard key={p.id} p={p} onApply={apply} canEdit={canEdit} />)}
            {data.unmatched.length > 0 && <div className="space-y-1.5"><div className="text-xs font-semibold text-ink">Persons named in documents, not yet parties</div>{data.unmatched.map((u, i) => <Unmatched key={`${u.documentId}-${i}`} u={u} onAdd={add} canEdit={canEdit} />)}</div>}
          </div>
          <div className="lg:col-span-2">
            <div className="text-xs font-semibold text-ink">Preview (only confirmed values; placeholders mark unknowns)</div>
            <pre className="nyk-scroll mt-1 max-h-96 overflow-auto whitespace-pre-wrap rounded-lg border border-line bg-surface p-3 font-display text-[13px] leading-relaxed text-ink" data-testid="cause-title-preview">{r.text}</pre>
            {r.excluded.length > 0 && <div className="mt-1 text-[11px] text-ink-muted">Not in cause title: {r.excluded.map((x) => `${x.name} (${x.role})`).join(", ")}</div>}
          </div>
        </div>
      </Panel>
    </div>
  );
}
